import { useState } from "react";
import { router } from "@inertiajs/react";
import { Button, Card, Code, Flex, Text, TextArea, TextField } from "@radix-ui/themes";
import { ManagerGallery } from "@/types/manager_gallery";

interface Props {
  gallery: ManagerGallery;
  limits: { presented_by: number; blurb: number };
  updatePath: string;
}

// The two pieces of copy the public gallery masthead renders. Unlike the images
// and the playlist — which write on every drop and every reorder — these are a
// deliberate edit, so they sit behind a Save rather than firing per keystroke.
export default function GalleryDetailsForm({ gallery, limits, updatePath }: Props) {
  const [presentedBy, setPresentedBy] = useState(gallery.presented_by ?? "");
  const [blurb, setBlurb] = useState(gallery.blurb ?? "");
  const [saving, setSaving] = useState(false);

  const dirty =
    presentedBy !== (gallery.presented_by ?? "") || blurb !== (gallery.blurb ?? "");

  function save() {
    setSaving(true);
    router.patch(
      updatePath,
      { gallery: { presented_by: presentedBy, blurb } },
      { preserveScroll: true, onFinish: () => setSaving(false) },
    );
  }

  return (
    <Card>
      <Flex direction="column" gap="4">
        <Flex direction="column" gap="1">
          <Text as="label" size="2" weight="medium" htmlFor="gallery-presented-by">
            Presented by
          </Text>
          <TextField.Root
            id="gallery-presented-by"
            value={presentedBy}
            maxLength={limits.presented_by}
            placeholder="Valino"
            onChange={(e) => setPresentedBy(e.target.value)}
          />
          <Text size="1" color="gray">
            The sponsor credit above the gallery. Leave it empty and the line is
            dropped entirely.
          </Text>
        </Flex>

        <Flex direction="column" gap="1">
          <Text as="label" size="2" weight="medium" htmlFor="gallery-blurb">
            Blurb
          </Text>
          <TextArea
            id="gallery-blurb"
            value={blurb}
            rows={5}
            maxLength={limits.blurb}
            placeholder="A short thank-you, and anything visitors should know before they start saving photos."
            onChange={(e) => setBlurb(e.target.value)}
          />
          <Flex justify="between" gap="3">
            <Text size="1" color="gray">
              Shown under the credit, in one narrow column. Formats with Markdown:{" "}
              <Code size="1">**bold**</Code>, <Code size="1">[text](https://…)</Code>{" "}
              for a link, <Code size="1">- item</Code> for a list. A new line is a new
              line.
            </Text>
            <Text size="1" color={blurb.length >= limits.blurb ? "amber" : "gray"}>
              {blurb.length} / {limits.blurb}
            </Text>
          </Flex>
        </Flex>

        <Flex justify="end">
          <Button onClick={save} disabled={!dirty || saving}>
            {saving ? "Saving…" : "Save details"}
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
}
